// src/App.jsx
import React, { useState } from 'react';
import { useApp } from './context/AppContext';
import { useAuth } from './context/AuthContext';
import PageLayout from './components/PageLayout';
import { Routes, Route, Navigate } from 'react-router-dom';
import Checkout from './pages/Checkout';
import QuickRent from './pages/QuickRent';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import OrdersPage from './pages/OrdersPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Loader2 } from 'lucide-react';

function App() {
  const { rentmanData, loading, error, rentmanLoading } = useApp();
  const { currentUser, loading: authLoading } = useAuth();
  const [isCartOpen, setIsCartOpen] = useState(false);

  React.useEffect(() => {
    const handler = () => setIsCartOpen(true);
    window.addEventListener('openCartSidebar', handler);
    return () => window.removeEventListener('openCartSidebar', handler);
  }, []);

  // Show loading state only while fetching Rentman data
  if (loading) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="font-medium text-foreground">Loading application data...</p>
            {rentmanLoading && <p className="text-sm text-muted-foreground">Fetching equipment data</p>}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) return <PageLayout>Error: {error.message}</PageLayout>;
  if (!rentmanData) return <PageLayout>No data available.</PageLayout>;

  return (
    <Routes>
      {/* Public route - main app */}
      <Route path="/" element={
        <PageLayout>
          <QuickRent isCartOpen={isCartOpen} onCartClose={() => setIsCartOpen(false)} />
        </PageLayout>
      } />
      
      {/* Protected routes - require authentication */}
      <Route path="/checkout" element={
        <ProtectedRoute>
          <PageLayout>
            <Checkout />
          </PageLayout>
        </ProtectedRoute>
      } />

      {/* Profile route - requires authentication */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <PageLayout>
            <ProfilePage />
          </PageLayout>
        </ProtectedRoute>
      } />

      {/* Orders route - requires authentication */}
      <Route path="/orders" element={
        <ProtectedRoute>
          <PageLayout>
            <OrdersPage />
          </PageLayout>
        </ProtectedRoute>
      } />

      {/* Admin route - requires authentication and admin role */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <PageLayout>
            <AdminPage />
          </PageLayout>
        </ProtectedRoute>
      } />

      {/* Reset Password route - public */}
      <Route path="/reset-password" element={
        <PageLayout>
          <ResetPasswordPage />
        </PageLayout>
      } />

      {/* Catch all route - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
